import { useCallback, useState, useRef } from 'react'
import type { UUID } from 'crypto'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useStep } from '../hooks/useStep'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useOrgDocs } from '@/utils/hooks/orgs'
import { useUploadOrgDocs } from '../hooks/useUploadDocs'
import UploadStep from '../components/UploadStep'

export default function UploadResources() {
  const { orgUuid, maxStepReached, setStaleFrom } = useOnboardingV2()
  const { token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''
  const { data: docs, deleteOrgDoc } = useOrgDocs(orgUuid as UUID, token)
  const uploadedDocs = docs?.filter((d) => !d.website_url)
  const [isUploading, setIsUploading] = useState(false)
  const uploadMutation = useUploadOrgDocs(orgUuid, token)
  const filesChangedRef = useRef(false)

  useStep({
    isValid: !isUploading,
    beforeNext: async () => {
      if (
        filesChangedRef.current &&
        maxStepReached >= STEPS.PROFILE_GENERATING
      ) {
        setStaleFrom((prev) =>
          prev === null
            ? STEPS.PROFILE_GENERATING
            : Math.min(prev, STEPS.PROFILE_GENERATING),
        )
      }
    },
  })

  const handleUploadFiles = useCallback(
    async (files: File[]): Promise<Array<{ id: string }>> => {
      filesChangedRef.current = true
      return uploadMutation.mutateAsync(files)
    },
    [uploadMutation],
  )

  const handleDeleteFile = useCallback(
    async (docId: string): Promise<void> => {
      filesChangedRef.current = true
      await deleteOrgDoc.mutateAsync({ token, docId: Number(docId) })
    },
    [deleteOrgDoc, token],
  )

  return (
    <UploadStep
      title="Upload resources"
      description="Upload annual reports, strategic plans, or other documents that describe your organisation."
      skipBanner={
        uploadedDocs?.length === 0
          ? "Don't have any documents? No worries — you can skip this step. But if you do have documents, please upload them! It really helps create a rich profile to find you better funding matches."
          : undefined
      }
      skipHintBottom="No documents to upload? Click Continue to skip this step."
      docs={uploadedDocs}
      onUploadFiles={handleUploadFiles}
      onDeleteFile={handleDeleteFile}
      onUploadingChange={setIsUploading}
    />
  )
}
